import React, { useContext, useRef, useEffect, useLayoutEffect, useState } from "react";
import styles from "./Players.module.scss";
import { Context } from "../Store";
import { isMobile } from "react-device-detect";
import useDragScroll from "../hooks/useDragScroll";
import useInfiniteTrack from "../hooks/useInfiniteTrack";

// ─── DEV ONLY: set to true to show overlay player-count tester ───────────────
const DEV_TEST_OVERLAY = false;
// ─────────────────────────────────────────────────────────────────────────────

const makeFakePlayers = (count) =>
  Array.from({ length: count }, (_, i) => ({
    id: `fake-${i}`,
    name:
      ["alice", "bob", "charlie", "diana", "eddie", "fiona", "george", "hannah", "ivan", "julia", "kevin", "lara"][i] ||
      `player ${i + 1}`,
    photo: null,
    gender: i % 2 === 0 ? "male" : "female",
    points: 10 - i,
    gold: 0,
    silver: 0,
    bronze: 0,
    team_id: "fake-team",
  }));

const FAKE_TEAM = { id: "fake-team", name: "Test Team" };

const Players = (props) => {
  const [state] = useContext(Context);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [overlayTeam, setOverlayTeam] = useState(null);
  const [devCount, setDevCount] = useState(4);

  const isTeams = state.type == "1";
  const players =
    isTeams && selectedTeam
      ? [...state.players]
          .filter((p) => p.team_id == selectedTeam.id)
          .sort((a, b) => b.points - a.points || b.gold - a.gold || b.silver - a.silver)
      : state.players;

  const trackRef = useRef();
  const teamTrackRef = useRef();
  const scrollSaveRef = useRef(null);
  const centeredTeamRef = useRef(null);
  const [trackOverflows, setTrackOverflows] = useState(false);

  useDragScroll(trackRef);
  useDragScroll(teamTrackRef);

  // Infinite loop only for desktop (non-teams)
  const loopingPlayers = useInfiniteTrack(isTeams ? null : trackRef, "players");
  const displayPlayers = loopingPlayers ? [...players, ...players, ...players] : players;

  // Check if the players track actually overflows so we know whether to centre or left-align
  useEffect(() => {
    const check = () => {
      const track = trackRef.current;
      if (!track) return;
      setTrackOverflows(track.scrollWidth > track.clientWidth + 1);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [players.length, loopingPlayers]);

  // Restore page scroll position after layout change (THE/TEAMS titles disappearing changes page height)
  useLayoutEffect(() => {
    if (scrollSaveRef.current !== null) {
      window.scrollTo(0, scrollSaveRef.current);
      scrollSaveRef.current = null;
    }
  });

  const handleSelectTeam = (team) => {
    scrollSaveRef.current = window.scrollY;
    if (team === null || (selectedTeam && selectedTeam.id === team.id)) {
      centeredTeamRef.current = null;
      setSelectedTeam(null);
    } else {
      setSelectedTeam(team);
    }
  };

  // Centre the selected team circle in the track.
  // Always sets the midpoint scroll first so centering is correct even if the
  // hook's midpoint effect didn't re-fire (looping state didn't cycle through false).
  useEffect(() => {
    if (!selectedTeam) {
      centeredTeamRef.current = null;
      return;
    }
    if (centeredTeamRef.current === selectedTeam.id) return;
    const track = teamTrackRef.current;
    if (!track) return;
    // Wait a frame so the DOM has painted before measuring
    requestAnimationFrame(() => {
      const item = track.querySelector(`[data-team-id="${selectedTeam.id}"]`);
      if (!item) return;
      const target = item.offsetLeft + item.offsetWidth / 2 - track.clientWidth / 2;
      track.scrollTo({ left: target, behavior: "smooth" });
      centeredTeamRef.current = selectedTeam.id;
    });
  }, [selectedTeam]);

  // Lock body scroll when overlay is open
  useEffect(() => {
    if (overlayTeam) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [overlayTeam]);

  const getPlayerPhoto = (player) => {
    if (player.photo) return `data:image/jpeg;base64, ${player.photo}`;
    return player.gender === "male" ? "./img/boy.png" : "./img/girl.png";
  };

  const scrollToLeaderboard = () => {
    const el = document.querySelector('.lbSection, [class*="lbSection"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Build sorted teams for type==1
  const sortedTeams = isTeams
    ? [...(state.teams || [])]
        .filter((team) => state.players.some((p) => p.team_id == team.id))
        .sort(
          (a, b) =>
            b.finalist - a.finalist ||
            a.final_result - b.final_result ||
            b.points - a.points ||
            b.medals_gold - a.medals_gold ||
            b.medals_silver - a.medals_silver,
        )
    : [];

  // Overlay geometry
  const OVERLAY_ITEM_SIZE = 85;
  const OVERLAY_NAME_HEIGHT = 24;
  const OVERLAY_CENTER_SIZE = 98;
  const OVERLAY_PAD = 8;

  // Ring radius — capped to available screen width
  const RING_R =
    typeof window !== "undefined"
      ? Math.min(145, Math.floor((window.innerWidth - 32 - OVERLAY_ITEM_SIZE - OVERLAY_PAD * 2) / 2))
      : 128;

  // How far above/below the ring the extra players sit (n=9-12)
  const EXTRA_PUSH = 155;

  // Returns {x, y} offsets from the centre circle centre for each player.
  // n≤8: all on one ring, evenly spaced from top.
  // n=9-12: ring players first, then fixed extras above, then below.
  //   9  → 5 ring + 3 above + 1 below
  //   10 → 7 ring + 2 above + 1 below
  //   11 → 7 ring + 3 above + 1 below
  //   12 → 8 ring + 2 above + 2 below
  const getPlayerPositions = (n) => {
    if (n === 0) return [];
    // n=2: side by side left/right
    if (n === 2)
      return [
        { x: -RING_R, y: 0 },
        { x: RING_R, y: 0 },
      ];
    // n=3: top, left, right (like n=4 with no bottom)
    if (n === 3)
      return [
        { x: 0, y: -RING_R - 20 },
        { x: -RING_R, y: 0 },
        { x: RING_R, y: 0 },
      ];

    const positions = [];
    let ringCount = n;
    let extrasAbove = 0;
    let extrasBelow = 0;
    if (n === 9) {
      ringCount = 5;
      extrasAbove = 3;
      extrasBelow = 1;
    }
    if (n === 10) {
      ringCount = 7;
      extrasAbove = 2;
      extrasBelow = 1;
    }
    if (n === 11) {
      ringCount = 7;
      extrasAbove = 3;
      extrasBelow = 1;
    }
    if (n === 12) {
      ringCount = 8;
      extrasAbove = 2;
      extrasBelow = 2;
    }

    // n=6: rotate so players pair up top/middle/bottom on each side
    //      use a taller ry to push the top and bottom pairs away from centre
    // n=7,8: use a larger circle radius so players are evenly spaced and not cramped
    const startAngle = n === 6 ? -(2 * Math.PI) / 3 : -Math.PI / 2;
    const ringRx = n === 7 || n === 8 || n === 10 || n === 11 || n === 12 ? RING_R + 10 : RING_R;
    const ringRy = n === 6 ? 150 : n === 7 || n === 8 || n === 10 || n === 11 || n === 12 ? RING_R + 40 : RING_R;

    // Ring players evenly spaced
    for (let i = 0; i < ringCount; i++) {
      const angle = startAngle + (i / ringCount) * 2 * Math.PI;
      const x = ringRx * Math.cos(angle);
      let y = ringRy * Math.sin(angle);
      // For 4 or 5 players, nudge the top player (i=0) a little higher
      if ((n === 4 || n === 5) && i === 0) y -= 20;
      // For n=9 (5-ring), nudge the top ring player further away from the centre circle
      if (n === 9 && i === 0) y -= 25;
      positions.push({ x, y });
    }

    // Extras above — upper-left, top-centre, upper-right of 8-ring, pushed outward
    if (extrasAbove === 2) {
      // Same x as the top-left and top-right ring players — shifted straight up
      [ringCount - 1, 1].forEach((idx) => {
        const angle = startAngle + (idx / ringCount) * 2 * Math.PI;
        const rx = ringRx * Math.cos(angle);
        const ry = ringRy * Math.sin(angle);
        positions.push({ x: rx, y: ry - EXTRA_PUSH });
      });
    } else if (extrasAbove === 3) {
      // Same x as the top-left, top, top-right ring players — just shifted straight up
      [ringCount - 1, 0, 1].forEach((idx) => {
        const angle = startAngle + (idx / ringCount) * 2 * Math.PI;
        const rx = ringRx * Math.cos(angle);
        const ry = ringRy * Math.sin(angle);
        positions.push({ x: rx, y: ry - EXTRA_PUSH });
      });
    }

    // Extras below
    if (extrasBelow === 1) {
      positions.push({ x: 0, y: ringRy + 90 });
    } else if (extrasBelow === 2) {
      // Same x as the bottom-left and bottom-right ring players — shifted straight down
      [Math.floor((ringCount * 5) / 8), Math.floor((ringCount * 3) / 8)].forEach((idx) => {
        const angle = startAngle + (idx / ringCount) * 2 * Math.PI;
        const rx = ringRx * Math.cos(angle);
        const ry = ringRy * Math.sin(angle);
        positions.push({ x: rx, y: ry + EXTRA_PUSH + 10 });
      });
    }

    return positions;
  };

  const overlayTeamPlayers =
    DEV_TEST_OVERLAY && overlayTeam
      ? makeFakePlayers(devCount)
      : overlayTeam
        ? [...state.players]
            .filter((p) => p.team_id == overlayTeam.id)
            .sort((a, b) => b.points - a.points || b.gold - a.gold || b.silver - a.silver)
        : [];
  const overlayN = overlayTeamPlayers.length;
  const overlayPositions = getPlayerPositions(overlayN);

  // Container bounds — centre circle sits at (0, 0) in our coord system
  let oTop = -OVERLAY_CENTER_SIZE / 2;
  let oBottom = OVERLAY_CENTER_SIZE / 2;
  let oHalfW = OVERLAY_CENTER_SIZE / 2;
  overlayPositions.forEach(({ x, y }) => {
    oTop = Math.min(oTop, y - OVERLAY_ITEM_SIZE / 2);
    oBottom = Math.max(oBottom, y + OVERLAY_ITEM_SIZE / 2 + OVERLAY_NAME_HEIGHT);
    oHalfW = Math.max(oHalfW, Math.abs(x) + OVERLAY_ITEM_SIZE / 2);
  });

  // Balance top/bottom so the centre circle always sits at the vertical midpoint —
  // prevents the overlay appearing high or low on screen when centred.
  const oExtent = Math.max(-oTop, oBottom);
  oTop = -oExtent;
  oBottom = oExtent;

  const overlayW = 2 * oHalfW + OVERLAY_PAD * 2;
  const overlayH = oBottom - oTop + OVERLAY_PAD * 2;
  const overlayCenterTop = -oTop + OVERLAY_PAD;

  return (
    <div
      style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/img/diamondsBgMob.svg)` }}
      className={styles.playersSection}
    >
      <div className={styles.subtitle}>
        Legends <br />
        of the Oche
      </div>

      {DEV_TEST_OVERLAY && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 16px",
            background: "#222",
            color: "#fff",
            fontSize: 13,
          }}
        >
          <strong>DEV:</strong> overlay player count:
          <button onClick={() => setDevCount((c) => Math.max(2, c - 1))} style={{ padding: "2px 8px" }}>
            −
          </button>
          <span style={{ minWidth: 20, textAlign: "center" }}>{devCount}</span>
          <button onClick={() => setDevCount((c) => Math.min(12, c + 1))} style={{ padding: "2px 8px" }}>
            +
          </button>
          <button
            onClick={() => setOverlayTeam(FAKE_TEAM)}
            style={{
              padding: "2px 8px",
              marginLeft: 8,
              background: "#e63",
              border: "none",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            Open overlay
          </button>
        </div>
      )}

      <div className={styles.teamContainer}>
        {isTeams && selectedTeam ? (
          // ── Team selected: circles replace THE/TEAMS text, photos below ──
          <>
            <div className={styles.playersTrack} ref={teamTrackRef}>
              {sortedTeams.map((team, index) => (
                <div
                  key={`${team.id}-${index}`}
                  data-team-id={team.id}
                  className={`${styles.playerItem}${selectedTeam.id === team.id ? ` ${styles.teamItemSelected}` : ""}`}
                  onClick={() => handleSelectTeam(selectedTeam.id === team.id ? null : team)}
                  style={{ cursor: "pointer" }}
                >
                  <div className={styles.teamCircle}>
                    <div className={styles.teamCircleName}>{team.name}</div>
                    <img
                      src={selectedTeam.id === team.id ? "./img/yellow-cross.svg" : "./img/pink-dart.svg"}
                      alt="dart"
                      className={styles.teamDart}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className={`${styles.playersTrack} ${styles.teamsPlayers}`} ref={trackRef}>
              {players.map((player, index) => (
                <div key={`${player.id}-${index}`} className={styles.playerItem}>
                  <div className={styles.playerImageSizer}>
                    <img src={getPlayerPhoto(player)} alt="" draggable="false" />
                  </div>
                  <p className={styles.playerName}>{player.name.toLowerCase()}</p>
                </div>
              ))}
            </div>
          </>
        ) : (
          // ── Default: THE text, circles/photos, TEAMS/DUEL text ──
          <>
            <div className={styles.topTitle}>
              {isTeams ? (
                <img src="./img/theText.svg" alt="The" className={styles.teamsText} />
              ) : (
                <img src="./img/theText.svg" alt="The" />
              )}
            </div>
            <div
              className={`${styles.playersTrack}${loopingPlayers ? ` ${styles.playersTrackLooping}` : ""}${!loopingPlayers && !trackOverflows ? ` ${styles.centred}` : ""}`}
              ref={trackRef}
            >
              <span data-sentinel-start style={{ flexShrink: 0, width: 1, height: "100%" }} />
              {isTeams
                ? sortedTeams.map((team) => (
                    <div
                      key={team.id}
                      data-team-id={team.id}
                      className={styles.playerItem}
                      onClick={() => (isMobile ? setOverlayTeam(team) : handleSelectTeam(team))}
                      style={{ cursor: "pointer" }}
                    >
                      <div className={styles.teamCircle}>
                        <div className={styles.teamCircleName}>{team.name}</div>
                        <img src="./img/pink-dart.svg" alt="dart" className={styles.teamDart} />
                      </div>
                    </div>
                  ))
                : displayPlayers.map((player, index) => (
                    <div key={`${player.id}-${index}`} className={styles.playerItem}>
                      <div className={styles.playerImageSizer}>
                        <img src={getPlayerPhoto(player)} alt="" draggable="false" />
                      </div>
                      <p className={styles.playerName}>{player.name.toLowerCase()}</p>
                    </div>
                  ))}
              <span data-sentinel-end style={{ flexShrink: 0, width: 1, height: "100%" }} />
            </div>
            <div className={styles.bottomTitle}>
              {isTeams ? (
                <img src="./img/teamsText.svg" alt="teams" className={styles.teamsText} />
              ) : state.players.length > 2 ? (
                <img src="./img/teamText.svg" alt="team" />
              ) : (
                <img src="./img/duelText.svg" alt="duel" />
              )}
            </div>
          </>
        )}
      </div>

      <div className={`${styles.viewMore}`} onClick={scrollToLeaderboard} style={{ cursor: "pointer" }}>
        View leaderboard
      </div>

      {overlayTeam && (
        <div className={styles.overlay} onClick={() => setOverlayTeam(null)}>
          <div className={styles.overlayInner} onClick={(e) => e.stopPropagation()}>
            <div
              className={styles.circleContainer}
              style={{
                width: overlayW,
                height: overlayH,
              }}
            >
              {/* Centre circle — styled like selected team circle, tap to close */}
              <div
                className={styles.circleCenter}
                style={{ top: overlayCenterTop }}
                onClick={() => setOverlayTeam(null)}
              >
                <span className={styles.circleCenterName}>{overlayTeam.name}</span>
                <img src="./img/yellow-cross.svg" alt="close" className={styles.circleCenterClose} />
              </div>

              {overlayPositions.map((pos, i) => {
                const player = overlayTeamPlayers[i];
                return (
                  <div
                    key={player.id}
                    className={styles.circlePlayer}
                    style={{
                      left: overlayW / 2 + pos.x - OVERLAY_ITEM_SIZE / 2,
                      top: overlayCenterTop + pos.y - OVERLAY_ITEM_SIZE / 2,
                    }}
                  >
                    <img src={getPlayerPhoto(player)} alt={player.name} />
                    <p className={styles.circlePlayerName}>{player.name.toLowerCase()}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Players;
